'use client';

import { GetActivitiesStatusFilter } from '@api/models';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';

import { Activity, getActivities } from '../services/activities.service';

/**
 * "Similar activities in this category" rail. Capped with a small `limit`: the
 * rail only shows a handful, and an uncapped fetch made the API run its per-row
 * slot-availability EXISTS + booking-count subqueries over the *entire*
 * category (100+ rows → 12–30s / 503s). We fetch `limit + 1` so excluding the
 * current activity still leaves `limit` to display.
 */
export const useActivitiesByCategory = (category: string, currentActivityId: string, limit = 10) => {
  const locale = useLocale();

  return useQuery({
    queryKey: ['activities', 'category', category, currentActivityId, locale, limit],
    queryFn: async () => {
      // Filter by category at the SQL layer (not client-side) so results are
      // not silently dropped by the backend page cap.
      const result = await getActivities({
        locale,
        statusFilter: GetActivitiesStatusFilter.approved,
        categoryId: category,
        limit: limit + 1
      });
      return result.activities.filter((activity: Activity) => activity.id !== currentActivityId).slice(0, limit);
    },
    enabled: !!category,
    staleTime: 60_000
  });
};
